import * as React from "react";
import { useEffect, useRef } from "react";

/* ------------------------------------------------------------------
   RoboCup SSL のフィールドを俯瞰視点で描き、ボールをつなぐプレーを
   ループ再生するライブ・テレメトリ風コンポーネント。
   samples/ssl-pitch.html のロジックを React に移植したもの。
   色は CSS 変数（--ssl-*）から読み取るため、外側のクラスで上書き可能。
------------------------------------------------------------------ */

const FIELD = {
  w: 9000, h: 6000, margin: 350,
  circle: 500,
  defenseW: 1000, defenseH: 2000,
  goalW: 1000, goalD: 180,
  robotR: 90, ballR: 43,
};

const BLUE = [
  [{ t: 0, x: -4250, y: 0 }, { t: .6, x: -4250, y: -120 }, { t: 1, x: -4250, y: -160 }],
  [{ t: 0, x: -1400, y: 1900 }, { t: .30, x: -200, y: 1650 }, { t: .6, x: 300, y: 1250 }, { t: 1, x: 620, y: 950 }],
  [{ t: 0, x: 300, y: -1500 }, { t: .42, x: 1880, y: -1160 }, { t: .62, x: 2300, y: -1000 }, { t: 1, x: 2520, y: -900 }],
  [{ t: 0, x: 1600, y: 520 }, { t: .55, x: 2740, y: -60 }, { t: .66, x: 3160, y: -170 }, { t: 1, x: 3420, y: -210 }],
  [{ t: 0, x: -900, y: -900 }, { t: .5, x: 700, y: -1900 }, { t: 1, x: 1420, y: -2100 }],
  [{ t: 0, x: -2600, y: -200 }, { t: .5, x: -2000, y: 100 }, { t: 1, x: -1600, y: 200 }],
];
const YELLOW = [
  [{ t: 0, x: 4250, y: 0 }, { t: .6, x: 4250, y: -140 }, { t: .68, x: 4250, y: -380 }, { t: 1, x: 4250, y: -400 }],
  [{ t: 0, x: 200, y: 2500 }, { t: .32, x: -60, y: 1800 }, { t: .6, x: 420, y: 1520 }, { t: 1, x: 760, y: 1300 }],
  [{ t: 0, x: -300, y: 900 }, { t: .35, x: 200, y: 1450 }, { t: 1, x: 900, y: 1620 }],
  [{ t: 0, x: 2500, y: 320 }, { t: .58, x: 3000, y: 60 }, { t: 1, x: 3320, y: -90 }],
  [{ t: 0, x: 1100, y: -700 }, { t: .5, x: 1760, y: -1200 }, { t: 1, x: 2220, y: -1400 }],
  [{ t: 0, x: 3300, y: 1300 }, { t: .5, x: 2700, y: 900 }, { t: 1, x: 2420, y: 700 }],
];
const PHASES = [
  { to: .24, name: "BUILD-UP" }, { to: .40, name: "PASS" }, { to: .55, name: "RECEIVE" },
  { to: .63, name: "PASS" }, { to: .70, name: "SHOT" }, { to: 1.0, name: "GOAL" },
];
const STILL_T = .665;

function ease(t) { return t * t * (3 - 2 * t); }
function lerp(a, b, t) { return a + (b - a) * t; }

function sample(kf, t) {
  if (t <= kf[0].t) return { x: kf[0].x, y: kf[0].y };
  const last = kf[kf.length - 1];
  if (t >= last.t) return { x: last.x, y: last.y };
  for (let i = 0; i < kf.length - 1; i++) {
    const a = kf[i], b = kf[i + 1];
    if (t >= a.t && t <= b.t) {
      const u = ease((t - a.t) / (b.t - a.t));
      return { x: lerp(a.x, b.x, u), y: lerp(a.y, b.y, u) };
    }
  }
  return { x: last.x, y: last.y };
}

function ballAt(t) {
  if (t < .28) { const c = sample(BLUE[1], t); return { x: c.x + 150, y: c.y - 40, fast: false }; }
  if (t < .40) {
    const a = sample(BLUE[1], .28), b = sample(BLUE[2], .40), u = (t - .28) / .12;
    return { x: lerp(a.x + 150, b.x, u), y: lerp(a.y - 40, b.y, u), fast: true };
  }
  if (t < .55) { const d = sample(BLUE[2], t); return { x: d.x + 120, y: d.y + 60, fast: false }; }
  if (t < .63) {
    const e = sample(BLUE[2], .55), f = sample(BLUE[3], .63), v = (t - .55) / .08;
    return { x: lerp(e.x + 120, f.x, v), y: lerp(e.y + 60, f.y, v), fast: true };
  }
  if (t < .70) {
    const g = sample(BLUE[3], .63), w = (t - .63) / .07;
    return { x: lerp(g.x, FIELD.w / 2, w), y: lerp(g.y, -300, w), fast: true };
  }
  const z = ease(Math.min(1, (t - .70) / .10));
  return { x: lerp(FIELD.w / 2, FIELD.w / 2 + 160, z), y: lerp(-300, -330, z), fast: false };
}

function pad4(n) {
  let s = Math.abs(Math.round(n)).toString();
  while (s.length < 4) s = "0" + s;
  return (n < 0 ? "-" : "+") + s;
}

function createPitch(host, options) {
  const opt = options || {};
  const loopMs = Number(opt.loop || 11000);
  const showHud = String(opt.hud || "on") !== "off";
  const playName = opt.play || "PLAY 07 逆サイド展開 → ワンタッチ";
  const label = opt.label ||
    "俯瞰視点のロボットサッカーのフィールド図。青いロボットがパスをつなぎ、ワンタッチでシュートを決めるプレーが繰り返し再生されます。";

  host.classList.add("ssl-pitch");
  host.innerHTML =
    '<figure class="ssl-pitch__stage" style="margin:0"><canvas role="img"></canvas></figure>' +
    (showHud
      ? '<div class="ssl-pitch__hud">' +
      '<span><b class="ssl-play"></b></span>' +
      '<span>PHASE <b class="ssl-phase">BUILD-UP</b></span>' +
      '<span>BALL <b class="ssl-num ssl-ball">x +0000 y +0000</b></span>' +
      '<span class="ssl-live">● 60 Hz</span>' +
      '</div>'
      : "");

  const canvas = host.querySelector("canvas");
  const ctx = canvas.getContext("2d");
  canvas.setAttribute("aria-label", label);

  const elPlay = host.querySelector(".ssl-play");
  const elPhase = host.querySelector(".ssl-phase");
  const elBall = host.querySelector(".ssl-ball");
  if (elPlay) elPlay.textContent = playName;

  const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
  let size = { w: 1, h: 1, dpr: 1 };
  const C = {};
  const trail = [];
  let raf = 0, t0 = 0, lastHud = 0, visible = true, running = false;

  function readTokens() {
    const cs = getComputedStyle(host);
    const v = (name, fallback) => cs.getPropertyValue(name).trim() || fallback;
    C.line = v("--ssl-line", "rgba(14,22,38,.42)");
    C.bound = v("--ssl-bound", "rgba(14,22,38,.16)");
    C.goal = v("--ssl-goal", "rgba(14,22,38,.55)");
    C.surface = v("--ssl-surface", "#EDF0EC");
    C.blue = v("--ssl-blue", "#1F4ED8");
    C.yellow = v("--ssl-yellow", "#FFC500");
    C.ball = v("--ssl-ball", "#FF5A00");
    C.lane = v("--ssl-lane", "rgba(31,78,216,.55)");
  }

  function measure() {
    const r = canvas.getBoundingClientRect();
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    size = { w: r.width, h: r.height, dpr: dpr };
    canvas.width = Math.max(1, Math.round(r.width * dpr));
    canvas.height = Math.max(1, Math.round(r.height * dpr));
  }

  function paint(t) {
    const W = size.w, H = size.h, dpr = size.dpr;
    if (W < 2) return null;
    const s = W / (FIELD.w + 2 * FIELD.margin);

    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, W, H);
    ctx.setTransform(s * dpr, 0, 0, -s * dpr, (W / 2) * dpr, (H / 2) * dpr);

    ctx.lineWidth = 8; ctx.strokeStyle = C.bound;
    ctx.strokeRect(-FIELD.w / 2 - FIELD.margin, -FIELD.h / 2 - FIELD.margin,
      FIELD.w + 2 * FIELD.margin, FIELD.h + 2 * FIELD.margin);

    ctx.lineWidth = 10; ctx.strokeStyle = C.line;
    ctx.strokeRect(-FIELD.w / 2, -FIELD.h / 2, FIELD.w, FIELD.h);
    ctx.beginPath(); ctx.moveTo(0, -FIELD.h / 2); ctx.lineTo(0, FIELD.h / 2); ctx.stroke();
    ctx.beginPath(); ctx.arc(0, 0, FIELD.circle, 0, Math.PI * 2); ctx.stroke();
    ctx.strokeRect(-FIELD.w / 2, -FIELD.defenseH / 2, FIELD.defenseW, FIELD.defenseH);
    ctx.strokeRect(FIELD.w / 2 - FIELD.defenseW, -FIELD.defenseH / 2, FIELD.defenseW, FIELD.defenseH);

    ctx.lineWidth = 20; ctx.strokeStyle = C.goal;
    ctx.strokeRect(-FIELD.w / 2 - FIELD.goalD, -FIELD.goalW / 2, FIELD.goalD, FIELD.goalW);
    ctx.strokeRect(FIELD.w / 2, -FIELD.goalW / 2, FIELD.goalD, FIELD.goalW);

    let lane = null;
    if (t > .16 && t < .28) lane = [sample(BLUE[1], t), sample(BLUE[2], .40)];
    else if (t > .47 && t < .55) lane = [sample(BLUE[2], t), sample(BLUE[3], .63)];
    else if (t > .58 && t < .63) lane = [sample(BLUE[3], t), { x: FIELD.w / 2, y: -300 }];
    if (lane) {
      ctx.save();
      ctx.setLineDash([70, 70]); ctx.lineWidth = 9; ctx.strokeStyle = C.lane;
      ctx.beginPath(); ctx.moveTo(lane[0].x, lane[0].y); ctx.lineTo(lane[1].x, lane[1].y); ctx.stroke();
      ctx.restore();
    }

    const ball = ballAt(t);

    if (ball.fast) { trail.push({ x: ball.x, y: ball.y }); if (trail.length > 26) trail.shift(); }
    else if (trail.length) { trail.shift(); }
    for (let i = 0; i < trail.length; i++) {
      ctx.globalAlpha = (i / trail.length) * .35;
      ctx.fillStyle = C.ball;
      ctx.beginPath(); ctx.arc(trail[i].x, trail[i].y, 34, 0, Math.PI * 2); ctx.fill();
    }
    ctx.globalAlpha = 1;

    function robot(p, prev, color, ours) {
      const dx = p.x - prev.x, dy = p.y - prev.y;
      const th = (dx * dx + dy * dy) > 4 ? Math.atan2(dy, dx) : Math.atan2(ball.y - p.y, ball.x - p.x);
      if (ours) {
        ctx.strokeStyle = C.lane; ctx.globalAlpha = .5; ctx.lineWidth = 6;
        ctx.beginPath(); ctx.arc(p.x, p.y, FIELD.robotR * 1.95, 0, Math.PI * 2); ctx.stroke();
        ctx.globalAlpha = 1;
      }
      ctx.beginPath();
      ctx.arc(p.x, p.y, FIELD.robotR, th + .84, th + Math.PI * 2 - .84);
      ctx.closePath();
      ctx.fillStyle = color; ctx.fill();
    }

    const back = Math.max(0, t - .008);
    for (let j = 0; j < YELLOW.length; j++) robot(sample(YELLOW[j], t), sample(YELLOW[j], back), C.yellow, false);
    for (let k = 0; k < BLUE.length; k++) robot(sample(BLUE[k], t), sample(BLUE[k], back), C.blue, true);

    const br = Math.max(FIELD.ballR, 5.5 / s);
    ctx.globalAlpha = .16; ctx.fillStyle = C.ball;
    ctx.beginPath(); ctx.arc(ball.x, ball.y, br * 2.4, 0, Math.PI * 2); ctx.fill();
    ctx.globalAlpha = 1;
    ctx.beginPath(); ctx.arc(ball.x, ball.y, br, 0, Math.PI * 2); ctx.fill();

    return ball;
  }

  function updateHud(ball, t) {
    if (!elBall) return;
    elBall.textContent = "x " + pad4(ball.x) + " y " + pad4(ball.y);
    for (let i = 0; i < PHASES.length; i++) {
      if (t <= PHASES[i].to) { elPhase.textContent = PHASES[i].name; break; }
    }
  }

  function frame(now) {
    raf = window.requestAnimationFrame(frame);
    if (!visible) return;
    const t = ((now - t0) % loopMs) / loopMs;
    const ball = paint(t);
    if (ball && now - lastHud > 90) { lastHud = now; updateHud(ball, t); }
  }

  function still() {
    readTokens(); measure();
    trail.length = 0;
    const ball = paint(STILL_T);
    if (ball) updateHud(ball, STILL_T);
  }

  function start() {
    if (running || mq.matches) return;
    running = true; t0 = performance.now();
    raf = window.requestAnimationFrame(frame);
  }
  function stop() {
    running = false;
    if (raf) window.cancelAnimationFrame(raf);
    raf = 0;
  }

  function onResize() {
    readTokens(); measure();
    if (!running) still();
  }

  let ro = null;
  if ("ResizeObserver" in window) {
    ro = new ResizeObserver(onResize);
    ro.observe(canvas);
  } else {
    window.addEventListener("resize", onResize);
  }

  let io = null;
  if ("IntersectionObserver" in window) {
    io = new IntersectionObserver((entries) => { visible = entries[0].isIntersecting; },
      { rootMargin: "150px" });
    io.observe(host);
  }

  function applyMotionPref() {
    if (mq.matches) { stop(); still(); } else { still(); start(); }
  }
  if (mq.addEventListener) mq.addEventListener("change", applyMotionPref);

  readTokens(); measure(); applyMotionPref();

  return {
    destroy() {
      stop();
      if (ro) ro.disconnect(); else window.removeEventListener("resize", onResize);
      if (io) io.disconnect();
      if (mq.removeEventListener) mq.removeEventListener("change", applyMotionPref);
      host.innerHTML = "";
      host.classList.remove("ssl-pitch");
    },
  };
}

function SSLPitch({ hud = "on", loop = 11000, play, label, className = "" }) {
  const ref = useRef(null);

  useEffect(() => {
    const host = ref.current;
    if (!host) return undefined;
    const pitch = createPitch(host, { hud, loop, play, label });
    return () => pitch.destroy();
  }, [hud, loop, play, label]);

  return <div ref={ref} className={className} />;
}

export default SSLPitch;
